// apps/web/src/features/clients/components/RequisitesSetFormModal.tsx
//
// Add or edit ONE requisites set of a legal entity (#2242 FR-2). The edit
// path is ID-addressed, so saving one set never rewrites its siblings.
//
// INVARIANT: only the original requisites text is authored here. Legacy
// structured bank values stay stored and wire-compatible, but this editor
// omits them so a text edit cannot overwrite or clear historical data.
//
// INVARIANT (AC-9): a create whose outcome is unknown (no server verdict) is
// NOT retried. The editor keeps the draft, refreshes the entity's existing
// ID-addressed sets IN PLACE and lets the operator read them, because a blind
// replay can leave the entity with two identical sets and no text comparison
// can prove which row is theirs. An edit is addressed at one known set id, so a
// missing verdict there is an ordinary retry, not an unresolved outcome.
//
// INVARIANT (AC-9): reading that list is NOT consent to send the create again.
// Save/Enter stay blocked afterwards; creating a second set is a separate,
// named decision behind a prompt that says a duplicate may appear. Leaving the
// editor is always available, so nobody is trapped here.
import { useId, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  LEGAL_ENTITY_REQUISITES_DETAILS_MAX_LENGTH,
  LegalEntityRequisitesInputSchema,
  type ClientLegalEntity,
  type LegalEntityRequisites,
  type LegalEntityRequisitesInput,
  type PatchLegalEntityRequisitesInput,
} from '@magimp/shared';
import {
  useAddLegalEntityRequisites,
  useClient,
  usePatchLegalEntityRequisites,
} from '@magimp/shared-hooks';
import { Banner, Button, ConfirmModal, EditorModal, FormRow, Textarea } from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { useClientFieldError } from '../useClientFieldError.js';
import { useClientSubmitError } from '../useClientSubmitError.js';
import { useMobileFieldStyle, useTouchActionStyle } from '../useMobileFieldStyle.js';
import { FieldLengthHint } from './FieldLengthHint.js';
import { EditorCancelButton } from './EditorCancelButton.js';
import { RequisitesInspectPanel } from './RequisitesInspectPanel.js';

type RequisitesForm = { details: string };

export interface RequisitesSetFormModalProps {
  clientId: string;
  entity: ClientLegalEntity;
  /** Present in edit mode; absent adds a new set to the entity. */
  set?: LegalEntityRequisites;
  onClose: () => void;
}

export function RequisitesSetFormModal({
  clientId,
  entity,
  set,
  onClose,
}: RequisitesSetFormModalProps) {
  const { t } = useTranslation(['clients', 'common']);
  const showToast = useToastStore((s) => s.show);
  const add = useAddLegalEntityRequisites(clientId, entity.id);
  const patch = usePatchLegalEntityRequisites(clientId, entity.id, set?.id ?? '__none__');
  const clientQ = useClient(clientId);
  const submitError = useClientSubmitError();
  const fieldError = useClientFieldError();
  const fieldStyle = useMobileFieldStyle();
  const actionStyle = useTouchActionStyle();
  const detailsCounterId = useId();
  const formId = useId();
  const isEdit = !!set;
  const [inspectSets, setInspectSets] = useState<readonly LegalEntityRequisites[] | null>(null);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, dirtyFields, isDirty },
  } = useForm<RequisitesForm>({
    resolver: zodResolver(LegalEntityRequisitesInputSchema as never),
    defaultValues: {
      details: set?.details ?? '',
    },
  });

  const detailsLength = (watch('details') ?? '').length;
  const pending = add.isPending || patch.isPending;

  // A requisites set owns no globally-unique slot, so a conflict here is about
  // the archived parent/set — form-level, with no field to point at.
  const onError = (err: unknown) => submitError.report(err, undefined, { idempotent: isEdit });

  // An unresolved create blocks the ordinary Save/Enter path for good: a
  // refresh tells the operator what exists, it does not authorise a second
  // POST. The way forward is the named "create anyway" decision below.
  const blockedByUncertainty = submitError.state.uncertain;

  const runCreate = (values: RequisitesForm) => {
    const payload: LegalEntityRequisitesInput = { details: values.details };
    add.mutate(payload, {
      onSuccess: () => {
        reset(values);
        showToast({ tone: 'success', text: t('clients:toasts.requisitesAdded') });
        onClose();
      },
      onError,
    });
  };

  /** The deliberate duplicate: only reachable through the confirmation. */
  const createAnyway = handleSubmit((values) => {
    setConfirmDuplicate(false);
    submitError.clear();
    setInspectSets(null);
    setRefreshFailed(false);
    runCreate(values);
  });

  const onSubmit = (values: RequisitesForm) => {
    // The guard lives here, not only on the disabled button: any submit path
    // (Enter in a field, a stray programmatic submit) must respect an
    // unresolved create, or the entity ends up with two identical sets.
    if (blockedByUncertainty) return;
    submitError.clear();
    setInspectSets(null);
    setRefreshFailed(false);
    if (isEdit) {
      const payload: PatchLegalEntityRequisitesInput = {};
      if (dirtyFields.details) payload.details = values.details;
      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }
      patch.mutate(payload, {
        onSuccess: () => {
          reset(values);
          showToast({ tone: 'success', text: t('clients:toasts.requisitesSaved') });
          onClose();
        },
        onError,
      });
      return;
    }
    runCreate(values);
  };

  // Uncertain outcome: refresh the aggregate and show the entity's CURRENT sets
  // inside this editor, with the draft untouched. The operator judges whether
  // their set landed; the UI never matches rows by text on their behalf.
  const checkResult = () => {
    setRefreshFailed(false);
    void clientQ.refetch().then((result) => {
      const live = result.data?.legalEntities.find((e) => e.id === entity.id);
      if (!result.isSuccess || live === undefined) {
        // A failed refresh changes nothing: the draft and the uncertainty both
        // stay, and the same check can be run again.
        setRefreshFailed(true);
        return;
      }
      setInspectSets(live.requisitesSets ?? []);
      submitError.markInspected();
    });
  };

  return (
    <EditorModal
      open={true}
      title={
        isEdit
          ? t('clients:modals.editRequisitesTitle', { entity: entity.name })
          : t('clients:modals.addRequisitesTitle', { entity: entity.name })
      }
      width={520}
      narrowFullWidth
      dirty={isDirty}
      busy={pending}
      onConfirmClose={onClose}
      footer={
        <>
          <EditorCancelButton
            onClose={onClose}
            label={t('common:actions.cancel')}
            style={actionStyle}
          />
          <Button
            type="submit"
            form={formId}
            kind="primary"
            style={actionStyle}
            disabled={pending || blockedByUncertainty}
            data-testid="requisites-submit"
          >
            {isEdit ? t('common:actions.save') : t('common:actions.create')}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
        {submitError.state.text !== null && (
          <div style={{ marginBottom: 'var(--s-3)' }}>
            <Banner
              tone={submitError.state.uncertain ? 'warn' : 'error'}
              action={
                submitError.state.uncertain ? (
                  <Button
                    size="sm"
                    style={actionStyle}
                    onClick={checkResult}
                    disabled={clientQ.isFetching}
                    data-testid="requisites-check-result"
                  >
                    {t('clients:actions.checkResult')}
                  </Button>
                ) : undefined
              }
            >
              {submitError.state.text}
            </Banner>
          </div>
        )}
        {refreshFailed && (
          <div style={{ marginBottom: 'var(--s-3)' }}>
            <Banner tone="error">{t('clients:conflict.refreshFailed')}</Banner>
          </div>
        )}
        {inspectSets !== null && (
          <RequisitesInspectPanel
            entityName={entity.name}
            sets={inspectSets}
            onCreateAnyway={isEdit ? undefined : () => setConfirmDuplicate(true)}
          />
        )}

        <FormRow
          label={t('clients:fields.requisites')}
          hint={t('clients:hints.requisitesDetails')}
          error={fieldError(errors.details, {
            kind: 'text',
            max: LEGAL_ENTITY_REQUISITES_DETAILS_MAX_LENGTH,
          })}
        >
          <Controller
            control={control}
            name="details"
            render={({ field }) => (
              <Textarea
                ref={field.ref}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                rows={7}
                invalid={!!errors.details}
                ariaDescribedBy={detailsCounterId}
                style={fieldStyle}
              />
            )}
          />
        </FormRow>
        <FieldLengthHint
          id={detailsCounterId}
          length={detailsLength}
          max={LEGAL_ENTITY_REQUISITES_DETAILS_MAX_LENGTH}
        />
      </form>

      {confirmDuplicate && (
        <ConfirmModal
          danger
          narrowFullWidth
          title={t('clients:confirm.createAnywayTitle')}
          text={t('clients:confirm.createAnywayMessage')}
          confirmLabel={t('clients:actions.createAnyway')}
          onConfirm={() => void createAnyway()}
          onClose={() => setConfirmDuplicate(false)}
        />
      )}
    </EditorModal>
  );
}
